import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { useMemo, useRef } from 'react';

import { Badge, Box, Flex, Heading, Image, Text, useSlotRecipe } from '@chakra-ui/react';

import qs from 'qs';
import { SwiperOptions } from 'swiper/types';
import { v4 as uuid } from 'uuid';
import { urlJoinP } from 'url-join-ts';

import { dateHelpers, formatHelpers, ImageRatio, ImageSize } from '@nocowanie/core';
import { Slider, usePluralisationTranslation } from '@nocowanie/common-ui';

import { AverageRating } from '@app/components/atoms/average-rating';
import { environment } from '@app/environments/environment';
import { useWpResizer } from '@app/hooks';
import { AccommodationItemVariantEnum } from '@app/models/accommodation-item.model';
import { ReservationTypeEnum } from '@app/models/reservation-type.model';

import './accommodation-item.scss';
import { AccommodationItemProps } from './accommodation-item.props';

import { AccommodationBadges, AccommodationBadgeType } from '../accommodation-badges';

export const AccommodationItem = ({
    id,
    name,
    settlementName,
    images,
    distanceFromCityCentre,
    path,
    ratingsCount,
    ratingsAverage,
    ratingDescription,
    cheapestRate: {
        rate,
        baseRate,
        guests,
        children,
        childrenAges,
        duration,
        currency,
        checkIn,
        checkOut,
    },
    filters: { freeCancel, provision, withoutPrepayment } = {},
    checkInDate,
    checkOutDate,
    additionalFilters: { flexDates } = {},
    reservationType,
    variant,
    isUnavailable,
    ...rest
}: AccommodationItemProps) => {
    const { t } = useTranslation('common', { keyPrefix: 'accommodationLabels' });
    const wpresizer = useWpResizer();
    const defaultImage = images.find(image => image.default);
    const pluralizeTranslation = usePluralisationTranslation('common', 'accommodationLabels');
    const hasDiscount = rate < baseRate;
    const discount = hasDiscount ? Math.floor(((baseRate - rate) / baseRate) * 100) : undefined;
    const nightsLabel = pluralizeTranslation(duration, 'night');
    const valueToPluralize = children && children > 0 ? children : guests;
    const totalGuestsLabel = pluralizeTranslation(
        valueToPluralize,
        'person',
        `${guests}${children ? ` + ${children}` : ''}`,
    );
    const isMapVariant = variant === AccommodationItemVariantEnum.Map;

    const accomodationHref = useMemo(() => {
        const params = {
            checkInDate: dateHelpers.format(
                checkIn ? new Date(checkIn) : (checkInDate ?? dateHelpers.defaultCheckInDate()),
                environment.API_DATE_FORMAT,
            ),
            checkOutDate: dateHelpers.format(
                checkOut ? new Date(checkOut) : (checkOutDate ?? dateHelpers.defaultCheckOutDate()),
                environment.API_DATE_FORMAT,
            ),
            adultsCount: guests?.toString() || '1',
            childrenCount: children ? children.toString() : '0',
            childrenAges: childrenAges?.map(String),
            freeCancel: freeCancel ? '1' : undefined,
            flexDates: flexDates ?? undefined,
            provision: provision?.map(String),
            withoutPrepayment: withoutPrepayment ? '1' : undefined,
        };
        const urlParams = qs.stringify(params, { arrayFormat: 'indices' });
        return urlJoinP(`/${path}`, [`?${urlParams}`]);
    }, [
        checkIn,
        checkInDate,
        checkOut,
        checkOutDate,
        guests,
        children,
        childrenAges,
        freeCancel,
        flexDates,
        provision,
        withoutPrepayment,
        path,
    ]);

    const recipe = useSlotRecipe({ key: 'accommodationItem' });
    const styles = recipe({
        variant: variant,
    });

    const badgesToShow: AccommodationBadgeType[] = [
        ...(reservationType === ReservationTypeEnum.Query
            ? [AccommodationBadgeType.ConfirmationUpTo24Hours]
            : []),
        ...(reservationType === ReservationTypeEnum.RealTime
            ? [AccommodationBadgeType.InstantReservation]
            : []),
    ];

    const linkRef = useRef<HTMLAnchorElement>(null);

    const sliderParams: SwiperOptions = {
        navigation: true,
        pagination: {
            type: 'bullets',
            dynamicBullets: true,
        },
        loop: true,
        allowTouchMove: true,
        simulateTouch: false,
    };

    const imagesList = useMemo(
        () =>
            images?.map((image, index) => {
                return {
                    id: uuid(),
                    body: (
                        <Image
                            src={wpresizer(
                                { ratio: ImageRatio['3/2'], size: ImageSize.Sm },
                                image.url,
                            )}
                            height="100%"
                            width="100%"
                            objectFit="cover"
                            alt={`${name ? name : t('altTextPlaceholder')} (${index + 1})`}
                            fetchPriority={index > 0 ? undefined : 'high'}
                            loading={index > 0 ? 'lazy' : undefined}
                        />
                    ),
                };
            }),
        [images],
    );

    const handleSlideClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;

        if (target.matches('img, span')) {
            linkRef.current?.click();
        }
    };

    return (
        <Flex css={styles.container} data-testid="accommodation-item-container" {...rest}>
            {isMapVariant ? (
                <Box
                    aspectRatio={'3 / 2'}
                    maxH={'185px'}
                    maxW={'100%'}
                    cursor={'pointer'}
                    onClick={handleSlideClick}
                    data-testid="accommodation-item-slider"
                    overflow={'hidden'}
                >
                    {badgesToShow?.length ? (
                        <AccommodationBadges
                            display={'flex'}
                            flex={1}
                            badges={badgesToShow}
                            css={styles.reservationTypeBadge}
                            data-testid="accommodation-item-badge"
                            iconProps={{ fontSize: '1.25em' }}
                        />
                    ) : null}

                    {imagesList ? (
                        <Slider
                            slides={imagesList}
                            sliderParams={sliderParams}
                            slideMinHeight="100%"
                            slideHeight={'100%'}
                        />
                    ) : null}
                </Box>
            ) : defaultImage?.url ? (
                <Image
                    src={wpresizer([300, 195], defaultImage.url)}
                    loading="lazy"
                    css={styles.image}
                    alt={name}
                    data-testid="accommodation-item-image"
                />
            ) : null}

            <Flex css={styles.contentWrapper}>
                <Flex flexDirection={'column'} css={styles.accommodationInfoWrapper}>
                    <Heading as={'h3'} css={styles.heading} data-testid="accommodation-item-header">
                        <Box asChild css={styles.link} data-testid="accommodation-item-link">
                            <Link
                                ref={linkRef}
                                href={accomodationHref}
                                prefetch={isMapVariant ? false : undefined}
                                target={isMapVariant ? '_blank' : undefined}
                                rel={isMapVariant ? 'noopener' : undefined}
                            >
                                {name}
                            </Link>
                        </Box>
                    </Heading>
                    {!isMapVariant ? (
                        <Flex css={styles.settlementNameContainer}>
                            <Text as={'span'} fontWeight={'bold'}>
                                {settlementName}
                            </Text>
                            {distanceFromCityCentre ? (
                                <Text as={'span'} color={'gray.500'}>
                                    {t('distanceFromCityCentre', { value: distanceFromCityCentre })}
                                </Text>
                            ) : null}
                        </Flex>
                    ) : null}
                </Flex>
                <Flex css={styles.cheapestRateAndRatingWrapper}>
                    {ratingsCount && ratingsAverage && ratingDescription ? (
                        <AverageRating
                            ratingsAverage={ratingsAverage}
                            ratingsCount={ratingsCount}
                            ratingDescription={ratingDescription}
                        />
                    ) : null}
                    {rate > 0 && !isUnavailable ? (
                        <Flex css={styles.cheapestRateWrapper}>
                            <Box css={styles.discountBadgeWrapper}>
                                {hasDiscount && !isMapVariant ? (
                                    <Badge
                                        variant={'solid'}
                                        size={'sm'}
                                        fontSize={'xs'}
                                        data-testid="accommodation-item-discount-value"
                                        css={styles.discountBadge}
                                    >
                                        -{discount}%
                                    </Badge>
                                ) : null}
                            </Box>
                            {isMapVariant && flexDates && checkIn && checkOut ? (
                                <Badge
                                    size={'sm'}
                                    variant={'outline'}
                                    color={'gray.900'}
                                    fontWeight={'normal'}
                                    mb={0.5}
                                    css={{ '--shadow-color': '#717171' }}
                                >
                                    {`${dateHelpers.format(new Date(checkIn), 'EEE d MMM')} - ${dateHelpers.format(new Date(checkOut), 'EEE d MMM')}`}
                                </Badge>
                            ) : null}
                            <Flex flexDirection={'row'} alignItems={'baseline'}>
                                {hasDiscount ? (
                                    <Text
                                        color={'gray.600'}
                                        mr={2}
                                        textDecoration="line-through"
                                        fontSize={'sm'}
                                        data-testid="accommodation-item-baserate"
                                    >
                                        {formatHelpers.price(baseRate, currency)}
                                    </Text>
                                ) : null}
                                <Text
                                    color={hasDiscount ? 'brand.500' : 'gray.900'}
                                    fontWeight={'bold'}
                                    data-testid="accommodation-item-rate"
                                >
                                    {formatHelpers.price(rate, currency)}
                                </Text>
                            </Flex>
                            <Text
                                as={'span'}
                                fontSize={'xs'}
                                data-testid="accommodation-item-guests-nights"
                            >
                                {totalGuestsLabel}, {nightsLabel}
                            </Text>
                        </Flex>
                    ) : null}
                    {isUnavailable ? (
                        <Text
                            as="span"
                            color={'brand.900'}
                            fontSize={'xs'}
                            alignSelf={'center'}
                            textAlign={'right'}
                            maxW={'115px'}
                            ml={'auto'}
                        >
                            {t('accommodationUnavailable')}
                        </Text>
                    ) : null}
                </Flex>
            </Flex>
        </Flex>
    );
};
